import React from 'react'
import {EmptyList} from '../../common/EmptyList'
import {Button, createToast, Icon, THEME_COLORS} from 'ac_ui'
import {acAlert, acPlay} from 'ac-icon'
import Field from '../domain/Field'
import QueryResult from '../domain/QueryResult'
import {isMacintosh} from '../utils'
import {getStorageData} from '../../../../storage/methods'

interface IResultInTableProps {
  isUserResult: boolean
  data: QueryResult
  dataOther: QueryResult
}

const NULL_STRING = 'NULL'

const ResultInTable: React.FC<IResultInTableProps> = ({isUserResult, data, dataOther}): React.ReactElement => {
  const isCopyTextFormatMarkdown = getStorageData('IS_COPY_TEXT_FORMAT_MARKDOWN')

  const onCopy = (): void => {
    let textCopy = ''
    const rows1 = data?.rows || []
    const fields1 = data?.fields || []
    if (rows1?.length > 0) {
      textCopy = '| # |'
      for (let i = 0; i < fields1.length; i++) {
        textCopy += ' ' + fields1[i].name + ' |'
      }
      textCopy += '\n'
      textCopy += '| - |'
      for (let i = 0; i < fields1.length; i++) {
        let textMask = ''
        for (let j = 0; j < fields1[i].name.length; j++) {
          textMask += '-'
        }
        textCopy += ' ' + textMask + ' |'
      }
      textCopy += '\n'
      for (let i = 0; i < rows1.length; i++) {
        const row2 = rows1[i] as any[]
        textCopy += '| ' + (i + 1) + ' |'
        for (let j = 0; j < row2.length; j++) {
          textCopy += ' ' + (row2[j] === null ? 'NULL' : row2[j]) + ' |'
        }
        if (i !== rows1.length - 1) {
          textCopy += '\n'
        }
      }
    }
    navigator.clipboard.writeText(textCopy)
    createToast(<p className={'mb-0 '}>Скопировано</p>, {
      type: 'info',
      closeOnClick: true,
    })
  }

  const getCols = (fields: Field[]): string[] => {
    const cols: string[] = []
    for (const field of fields || []) {
      cols.push(field.name)
    }
    return cols
  }

  const getTableHeader = (cols): React.ReactElement => {
    const tableCols = [<th key={'_row_number'} className={'start-table-th'} />]
    cols.forEach((col, index) => {
      const otherFields = dataOther?.fields || []
      const isExists = index < otherFields.length
      const isNameEqual = isExists && col === otherFields[index].name
      let title: string
      if (!isNameEqual) {
        title = 'Название столбца отличается'
      }
      if (!isExists) {
        title = `Столбец отсутствует ${isUserResult ? 'в эталонном решении' : 'в решении пользователя'}`
      }
      tableCols.push(
        <th
          key={index + '_' + col}
          className={(isExists ? '' : 'column-non-existent') + (!isNameEqual ? ' ' + 'column-different-name' : '')}
          title={title}>
          {col === '?column?' ? 'unknown' : col}
        </th>,
      )
    })
    return (
      <thead>
        <tr>{tableCols}</tr>
      </thead>
    )
  }

  const renderValue = (value: any): any => {
    if (typeof value === 'boolean') {
      if (value === true) {
        return 'TRUE'
      }
      if (value === false) {
        return 'FALSE'
      }
      return NULL_STRING
    }
    if (Array.isArray(value)) {
      return JSON.stringify(value)
    }
    return value === null ? NULL_STRING : value
  }

  const getTableBody = (cols, rows: object[]): React.ReactElement => {
    const tableRows = []
    rows.forEach((row, indexRow) => {
      if (indexRow < 50) {
        const otherRows = dataOther?.rows || []
        const otherRow = otherRows[indexRow] || []
        const isRowExists = !!otherRows[indexRow]
        const tableCols = [
          <td key={'_row_number'} className={'header-column' + (!isRowExists ? ' value-row-non-existent' : '')}>
            {indexRow + 1}
          </td>,
        ]
        cols.forEach((col, index) => {
          const otherFields = dataOther?.fields || []

          const isColumnExists = index < otherFields.length
          const valueCurrent = row[index]
          const valueOther = otherRow[index]
          const isValueEqual = valueCurrent === valueOther
          let title: string
          let className: string
          if (!isValueEqual) {
            title = 'Значения отличаются'
            className = 'value-different'
          }
          if (!isColumnExists) {
            title = `Столбец отсутствует ${isUserResult ? 'в эталонном решении' : 'в решении пользователя'}`
            className = 'value-column-non-existent'
          }
          if (!isRowExists) {
            title = `Строка отсутствует ${isUserResult ? 'в эталонном решении' : 'в решении пользователя'}`
            className = 'value-row-non-existent'
          }
          tableCols.push(
            <td key={index + '_' + col} className={'white-space-pre-wrap ' + className} title={title}>
              {renderValue(row[index])}
            </td>,
          )
        })
        tableRows.push(<tr key={indexRow}>{tableCols}</tr>)
      }
    })
    return <tbody>{tableRows}</tbody>
  }

  let resultTable
  const rows = data?.rows || []
  let stats = ''
  if (!data) {
    resultTable = (
      <div className={'task-tab-content d-flex flex-column justify-content-center custom-scroll'}>
        <EmptyList
          type={'TABLE'}
          isMobileImg
          title={isUserResult ? 'Мой результат' : 'Эталонный результат'}
          description={
            <>
              Чтобы выполнить код, нажмите
              <br /> кнопку{' '}
              <span>
                <Icon
                  path={acPlay}
                  title={'Выполнить'}
                  className={'mt-n-x1'}
                  color={THEME_COLORS.success}
                  size={'20px'}
                />
              </span>{' '}
              или {isMacintosh() ? 'Cmd + Enter' : 'Ctrl + Enter'}
            </>
          }
        />
      </div>
    )
  } else if (data.error) {
    resultTable = (
      <div className={'task-result-table task-tab-content custom-scroll p-x6'}>
        <div className={'d-flex flex-row align-items-start'}>
          <div className={'d-flex mr-x1'}>
            <Icon path={acAlert} title={'Ошибка'} color={THEME_COLORS.danger} />
          </div>
          <p className={'task-result-error-p mb-0'}>{data.error}</p>
        </div>
      </div>
    )
  } else if (rows.length === 0) {
    const cols = getCols(data.fields)
    stats = `строк: ${rows.length} столбцов: ${cols.length}`
    resultTable = (
      <div className={'task-tab-content task-tab-content-table custom-scroll pr-x1'}>
        <table>{getTableHeader(cols)}</table>
      </div>
    )
  } else {
    const cols = getCols(data.fields)
    stats = `строк: ${rows.length} столбцов: ${cols.length}`
    resultTable = (
      <div className={'task-tab-content task-tab-content-table custom-scroll pr-x1'}>
        <table>
          {getTableHeader(cols)}
          {getTableBody(cols, rows)}
        </table>
      </div>
    )
  }

  return (
    <>
      {resultTable}
      <div className={'task-result-bottom'}>
        <div className={'d-flex flex-row align-items-center'}>
          <span className={'stats'}>{stats}</span>
          {isCopyTextFormatMarkdown && stats && (
            <Button size={'sm'} style={'link'} className={'ml-x2'} onClick={onCopy}>
              Скопировать
            </Button>
          )}
        </div>
        {isUserResult && (
          <span className={'run-command'}>Выполнить код: {isMacintosh() ? 'Cmd + Enter' : 'Ctrl + Enter'}</span>
        )}
      </div>
    </>
  )
}

export {ResultInTable}
